/**
 * Life4Earth V2 — Project Team & Role Types
 *
 * Type definitions for the three-tier role model:
 *   Tier 1: RoleDefinition (platform catalog)
 *   Tier 2: ProjectRoleSlot (project instances)
 *   Tier 3: ProjectRoleAssignment (people)
 *
 * Architecture ref: docs/features/Life4Earth_Project_Role_Slots_Architecture.docx
 */

/* ------------------------------------------------------------------ */
/*  Role Categories                                                    */
/* ------------------------------------------------------------------ */

export const ROLE_CATEGORIES = [
  'research',
  'labor',
  'leadership',
  'technical',
  'creative',
  'advocacy',
  'education',
  'data_collection',
  'coordination',
  'enablement',
  'custom',
] as const;

export type RoleCategory = (typeof ROLE_CATEGORIES)[number];

/* ------------------------------------------------------------------ */
/*  Dimension Weights                                                  */
/* ------------------------------------------------------------------ */

export interface DimensionWeights {
  mind: number;
  body: number;
  heart: number;
  wings: number;
  roots: number;
  voice: number;
}

export const DIMENSION_KEYS = ['mind', 'body', 'heart', 'wings', 'roots', 'voice'] as const;
export type DimensionKey = (typeof DIMENSION_KEYS)[number];

/** Parse a JSONB string into typed dimension weights. */
export function parseDimensionWeights(json: string | null | undefined): DimensionWeights {
  if (!json) return { mind: 0.2, body: 0.2, heart: 0.2, wings: 0.2, roots: 0.2, voice: 0.2 };
  try {
    const parsed = JSON.parse(json);
    return {
      mind: parsed.mind ?? 0.2,
      body: parsed.body ?? 0.2,
      heart: parsed.heart ?? 0.2,
      wings: parsed.wings ?? 0.2,
      roots: parsed.roots ?? 0.2,
      voice: parsed.voice ?? 0.2,
    };
  } catch {
    return { mind: 0.2, body: 0.2, heart: 0.2, wings: 0.2, roots: 0.2, voice: 0.2 };
  }
}

/* ------------------------------------------------------------------ */
/*  Resource Phase (matches PreflightItem phases)                      */
/* ------------------------------------------------------------------ */

export const ROLE_PHASES = ['preparation', 'operational', 'sustainment'] as const;
export type RolePhase = (typeof ROLE_PHASES)[number];

/* ------------------------------------------------------------------ */
/*  Role Slot Status                                                   */
/* ------------------------------------------------------------------ */

export const ROLE_SLOT_STATUSES = [
  'draft',
  'open',
  'partially_filled',
  'filled',
  'closed',
] as const;

export type RoleSlotStatus = (typeof ROLE_SLOT_STATUSES)[number];

/* ------------------------------------------------------------------ */
/*  Assignment Status                                                  */
/* ------------------------------------------------------------------ */

/**
 * Full 14-status assignment lifecycle (V2).
 *
 * Transitions:
 *   pending_invitation → active | declined | rescinded
 *   pending_approval   → active | rejected | withdrawn
 *   active             → pending_verification | resigned | removed | expired
 *   pending_verification → completed | pending_escalated_review
 *   pending_escalated_review → completed | revoked
 *   completed           → (terminal)
 *
 * Terminal statuses: completed, resigned, removed, expired, declined,
 *   rescinded, revoked, rejected, withdrawn
 *
 * Terminology:
 *   - declined:   invitee turns down an invitation
 *   - rescinded:  inviter withdraws an invitation before acceptance
 *   - withdrawn:  applicant withdraws their own pending_approval request
 *   - revoked:    authority rescinds an escalated-review assignment
 *   - rejected:   authority denies a pending_approval application
 *   - removed:    PM removes an active assignment
 */
export const ASSIGNMENT_STATUSES = [
  'pending_invitation',
  'pending_approval',
  'active',
  'pending_verification',
  'pending_escalated_review',
  'completed',
  'resigned',
  'removed',
  'expired',
  'declined',
  'rescinded',
  'revoked',
  'rejected',
  'withdrawn',
] as const;

export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

/** Statuses that represent an "active" engagement (role in progress). */
export const ACTIVE_STATUSES: AssignmentStatus[] = [
  'active', 'pending_verification', 'pending_escalated_review', 'pending_invitation',
];

/** Terminal statuses (role is done — one way or another). */
export const TERMINAL_STATUSES: AssignmentStatus[] = [
  'completed', 'resigned', 'removed', 'expired', 'declined', 'revoked', 'rejected', 'withdrawn',
];

/* ------------------------------------------------------------------ */
/*  Role Duration Types                                                */
/* ------------------------------------------------------------------ */

export const DURATION_TYPES = [
  'project_duration',   // Runs for the life of the project (default)
  'phase_duration',     // Runs for a specific project phase
  'fixed_term',         // Explicit start/end dates set by PM
  'ongoing',            // No end date (permanent maintenance, community liaison)
] as const;

export type DurationType = (typeof DURATION_TYPES)[number];

/* ------------------------------------------------------------------ */
/*  Visibility                                                         */
/* ------------------------------------------------------------------ */

export type RoleVisibility = 'internal' | 'public';

/* ------------------------------------------------------------------ */
/*  Member Visibility (per-assignment)                                 */
/* ------------------------------------------------------------------ */

/**
 * Controls who can see an assigned member's identity (name, avatar).
 *
 * - `public`    — Anyone viewing the project can see the member's identity.
 * - `team_only` — Only project team members, PM, Owner, and system admins
 *                 see identity. Others see "Team Member" + generic avatar.
 * - `anonymous` — Only PM/Owner/system admin sees identity.
 *                 Even fellow team members see "Anonymous Contributor".
 */
export type MemberVisibility = 'public' | 'team_only' | 'anonymous';

/**
 * Viewer permission tiers for visibility resolution.
 *
 * - `admin`  — PM, Owner, or system admin: sees all identities.
 * - `team`   — Active team member (has an active assignment): sees public + team_only.
 * - `public` — No special access: sees only public identities.
 */
export type ViewerTier = 'admin' | 'team' | 'public';

/**
 * Determines whether the viewer can see a member's real identity
 * given the assignment's visibility setting and the viewer's tier.
 */
export function canSeeIdentity(
  assignmentVisibility: string,
  viewerTier: ViewerTier
): boolean {
  // Admins always see everyone
  if (viewerTier === 'admin') return true;

  switch (assignmentVisibility) {
    case 'public':
      return true;
    case 'team_only':
      return viewerTier === 'team';
    case 'anonymous':
      return false;
    default:
      // Default to team_only behavior for unknown values
      return viewerTier === 'team';
  }
}

/* ------------------------------------------------------------------ */
/*  Skill Domain                                                       */
/* ------------------------------------------------------------------ */

export const SKILL_DOMAINS = [
  'environmental',
  'technical',
  'social',
  'administrative',
  'trade',
  'general',
] as const;

export type SkillDomain = (typeof SKILL_DOMAINS)[number];

/* ------------------------------------------------------------------ */
/*  Editor Mode (for shared RoleSlotEditor component)                  */
/* ------------------------------------------------------------------ */

export const EDITOR_MODES = [
  'catalog',    // Admin Console: full editor
  'workspace',  // Problem-Solving Phase 5: pre-filled from AI
  'template',   // Template Editor: definition fields
  'project',    // Project Work Tab: catalog picker + create
  'readonly',   // Volunteer Flow: display only
] as const;

export type EditorMode = (typeof EDITOR_MODES)[number];
